import { screen } from '@testing-library/dom';
import { useRef, useState, useCallback, useEffect } from 'react';

export const useInterviewMetrics = (question, interviewKey) => {
    const [answer, setAnswer] = useState('');
    const [isMonitoring, setIsMonitoring] = useState(false);
    const lastPasteSelectionRef = useRef(null);
    const [pasteDialog, setPasteDialog] = useState({ open: false, pasteData: null });

    const answerRef = useRef(answer);
    const textareaRef = useRef(null);
    // const questionRef = useRef(null);
    const preservedAnswerRef = useRef('');

    // Store question in a ref so it's always up-to-date
    const questionRef = useRef(question);

    // Update questionRef when question changes
    useEffect(() => {
        questionRef.current = question;
    }, [question]);

    useEffect(() => {
        answerRef.current = answer;
        preservedAnswerRef.current = answer;
    }, [answer]);

    // Technical terms dictionary
    const technicalTerms = [
        'algorithm', 'database', 'framework', 'api', 'endpoint', 'microservice',
        'container', 'kubernetes', 'docker', 'javascript', 'python', 'react',
        'nodejs', 'mongodb', 'postgresql', 'authentication', 'authorization',
        'middleware', 'optimization', 'scalability', 'latency', 'throughput',
        'devops', 'ci/cd', 'integration', 'deployment', 'architecture'
    ];

    // Helper function to convert timestamp to UTC string
    const toISOString = (timestamp) => {
        if (!timestamp) return null;
        return new Date(timestamp).toISOString();
    };

    // Metrics storage using class for better organization
    class InterviewMetrics {
        constructor(interviewKey) {
            this.interview_key = interviewKey;
            this.ai_interview_id = null;
            this.reset();
            // Add question-level metrics tracking
            this.question_start_time = null;
            this.question_metrics_snapshot = null;
            this.is_question_active = false;
        }

        reset() {
            // Session Information
            this.is_browser = true;
            this.platform = "web_browser";
            this.session_start_time = null;
            this.session_end_time = null;
            this.session_duration_ms = 0;
            this.user_agent = navigator.userAgent;
            this.question_start_time = null;
            this.question_metrics_snapshot = null;
            this.is_question_active = false;

            // AI Interview ID - keep it from constructor
            this.ai_interview_id = this.ai_interview_id || null;

            // Question tracking
            this.current_question_id = null;

            // Activity Patterns
            this.activity = {
                idle_duration_ms: 0,
                last_activity_time: null,
                total_active_time_ms: 0,
                inactivity_history: [],
                inactivity_timeout: null
            };

            // Visibility Tracking
            this.visibility = {
                unfocused_duration_ms: 0,
                app_switch_count: 0,
                app_switch_timestamps: [], // Add this line
                max_unfocused_duration_ms: 0,
                min_unfocused_duration_ms: null,
                unfocused_periods: [],
                consecutive_switches: 0,
                last_focus_loss_time: null,
                last_focus_gain_time: null,
                rapid_switch_threshold: 5000,
                last_switch_time: null
            };

            // Mouse Input Tracking
            this.mouse_input = {
                mouse_distance_px: 0,
                click_count: 0,
                hover_pause_count: 0,
                mouse_teleport_count: 0,
                clicks_outside_answer_area: 0,
                suspicious_click_patterns: 0,
                right_click_count: 0,
                last_selected_text: null,

                // Advanced Mouse Analytics
                precision_clicks: 0,
                mouse_idle_periods: [],
                scroll_behavior_anomalies: 0,
                recent_click_positions: [],
                max_same_position_clicks: 3,
                position_tolerance: 2,

                // Internal tracking
                mouse_down_position: null,
                last_mouse_position: { x: 0, y: 0 },
                last_mouse_move_time: null,
                mouse_idle_start_time: null,
                click_timestamps: [],
                last_selection_time: null,
                scroll_timestamps: [],
                last_scroll_time: null,
                hover_start_time: null,
                current_hover_element: null,

                // Thresholds
                teleport_threshold: 100,
                precision_threshold: 1,
                hover_pause_threshold: 1000,
                mouse_idle_threshold: 3000
            };

            // Text Input Analysis
            this.text_input = {
                char_typed_count: 0,
                backspace_count: 0,
                wpm: 0,
                max_keystroke_burst: 0,
                paste_count: 0,
                chars_pasted_count: 0,
                copy_event_count: 0,
                copy_cleanup_listener: null,
                unexplained_text_delta: 0,

                // Advanced Cheating Indicators
                paste_to_type_ratio: 0,
                rapid_typing_bursts: 0,
                inconsistent_typing_speed: 0,
                external_content_indicators: 0,

                // Content Analysis
                url_pattern_pastes: 0,

                // Word count tracking
                total_words: 0,

                // Internal tracking
                last_text_length: 0,
                last_word_count: 0
            };

            // Environment Integrity Tracking
            this.environment_integrity = {
                dom_mutation_count: 0,
                suspicious_dom_changes: 0,
                dev_tools_indicators: 0,
                window_resize_count: 0,
                console_usage_detected: 0,
                javascript_injection_attempts: 0,
                network_request_anomalies: 0,

                // Internal tracking
                original_dom_structure: null,
                mutation_observer: null,
                dev_tools_check_interval: null,
                console_override: null,
                resize_timeout: null,
                network_requests: new Set(),
                allowed_dom_elements: ['textarea', 'div', 'span', 'button', 'p', 'label'],
                restricted_elements: ['script', 'iframe', 'object', 'embed', 'applet'],

                // Properties for dev tools detection
                keydown_listener: null,
                original_console_clear: null
            };

            this.pasted_content = [];
            // event_timeline removed

            // Interval Tracking - IMPORTANT: Initialize as empty array
            this.interval_start_time = null;
            this.last_interval_snapshot = null;
            this.interval_metrics = []; // Initialize as empty array

            // Suspicion Score
            this.suspicion_score = 0;

            if (this.mouse_input.monacoCleanupListeners) {
                this.mouse_input.monacoCleanupListeners();
                this.mouse_input.monacoCleanupListeners = null;
            }

            if (this.mouse_input.monacoObserver) {
                this.mouse_input.monacoObserver.disconnect();
                this.mouse_input.monacoObserver = null;
            }

            // Clean up Monaco event listeners
            if (this.mouse_input.monacoEventListeners) {
                this.mouse_input.monacoEventListeners.forEach(listener => {
                    if (listener.element && listener.click) {
                        listener.element.removeEventListener('click', listener.click);
                    }
                    if (listener.element && listener.contextmenu) {
                        listener.element.removeEventListener('contextmenu', listener.contextmenu);
                    }
                });
                this.mouse_input.monacoEventListeners = [];
            }
        }

        startQuestionMetrics(questionId) {
            const now = Date.now();
            this.question_start_time = now;
            this.is_question_active = true;
            this.setCurrentQuestionId(questionId);

            // Take snapshot of current state
            this.question_metrics_snapshot = this.createQuestionSnapshot();

            console.log(`📝 Started question metrics tracking for question ${questionId}`);
        }

        // Create snapshot for question start
        createQuestionSnapshot() {
            return {
                timestamp: Date.now(),
                // Copy current metric values
                text_input: {
                    char_typed_count: this.text_input.char_typed_count,
                    backspace_count: this.text_input.backspace_count,
                    paste_count: this.text_input.paste_count,
                    chars_pasted_count: this.text_input.chars_pasted_count,
                    copy_event_count: this.text_input.copy_event_count,
                    total_words: this.text_input.total_words,
                    wpm: this.text_input.wpm
                },
                mouse_input: {
                    click_count: this.mouse_input.click_count,
                    hover_pause_count: this.mouse_input.hover_pause_count,
                    mouse_distance_px: this.mouse_input.mouse_distance_px,
                    right_click_count: this.mouse_input.right_click_count,
                    clicks_outside_answer_area: this.mouse_input.clicks_outside_answer_area
                },
                activity: {
                    total_active_time_ms: this.activity.total_active_time_ms,
                    idle_duration_ms: this.activity.idle_duration_ms
                },
                visibility: {
                    unfocused_duration_ms: this.visibility.unfocused_duration_ms,
                    app_switch_count: this.visibility.app_switch_count
                },
                environment_integrity: {
                    dev_tools_indicators: this.environment_integrity.dev_tools_indicators,
                    window_resize_count: this.environment_integrity.window_resize_count,
                    console_usage_detected: this.environment_integrity.console_usage_detected
                },
                pasted_content_count: this.pasted_content.length,
                suspicion_score: this.suspicion_score
            };
        }

        // Get metrics for the entire question duration
        getQuestionMetrics(action = 'submit') {
            if (!this.is_question_active || !this.question_start_time) {
                console.warn('No active question metrics to collect');
                return null;
            }

            const now = Date.now();
            const question_duration_ms = now - this.question_start_time;

            if (!this.question_metrics_snapshot) {
                console.warn('No question snapshot found');
                return null;
            }

            // Calculate deltas from question start
            const questionMetrics = {
                // Question metadata
                question_id: this.current_question_id,
                question_start_time: toISOString(this.question_start_time),
                question_end_time: toISOString(now),
                question_duration_ms: question_duration_ms,
                action: action, // 'submit' or 'skip'

                // Text input deltas (only what happened during this question)
                text_input: {
                    char_typed_count: this.text_input.char_typed_count - this.question_metrics_snapshot.text_input.char_typed_count,
                    backspace_count: this.text_input.backspace_count - this.question_metrics_snapshot.text_input.backspace_count,
                    paste_count: this.text_input.paste_count - this.question_metrics_snapshot.text_input.paste_count,
                    chars_pasted_count: this.text_input.chars_pasted_count - this.question_metrics_snapshot.text_input.chars_pasted_count,
                    copy_event_count: this.text_input.copy_event_count - this.question_metrics_snapshot.text_input.copy_event_count,
                    words_added: this.text_input.total_words - this.question_metrics_snapshot.text_input.total_words,
                    final_wpm: this.text_input.wpm
                },

                // Mouse input deltas
                mouse_input: {
                    click_count: this.mouse_input.click_count - this.question_metrics_snapshot.mouse_input.click_count,
                    hover_pause_count: this.mouse_input.hover_pause_count - this.question_metrics_snapshot.mouse_input.hover_pause_count,
                    mouse_distance_px: this.mouse_input.mouse_distance_px - this.question_metrics_snapshot.mouse_input.mouse_distance_px,
                    right_click_count: this.mouse_input.right_click_count - this.question_metrics_snapshot.mouse_input.right_click_count,
                    clicks_outside_answer_area: this.mouse_input.clicks_outside_answer_area - this.question_metrics_snapshot.mouse_input.clicks_outside_answer_area
                },

                // Activity deltas
                activity: {
                    total_active_time_ms: this.activity.total_active_time_ms - this.question_metrics_snapshot.activity.total_active_time_ms,
                    idle_duration_ms: this.activity.idle_duration_ms - this.question_metrics_snapshot.activity.idle_duration_ms
                },

                // Visibility deltas
                visibility: {
                    unfocused_duration_ms: this.visibility.unfocused_duration_ms - this.question_metrics_snapshot.visibility.unfocused_duration_ms,
                    app_switch_count: this.visibility.app_switch_count - this.question_metrics_snapshot.visibility.app_switch_count
                },

                // Environment integrity deltas
                environment_integrity: {
                    dev_tools_indicators: this.environment_integrity.dev_tools_indicators - this.question_metrics_snapshot.environment_integrity.dev_tools_indicators,
                    window_resize_count: this.environment_integrity.window_resize_count - this.question_metrics_snapshot.environment_integrity.window_resize_count,
                    console_usage_detected: this.environment_integrity.console_usage_detected - this.question_metrics_snapshot.environment_integrity.console_usage_detected
                },

                // Suspicion score for THIS QUESTION only
                suspicion_score: this.calculateSuspicionScoreForQuestion(),

                // Filter pasted content for this question only
                pasted_content: this.pasted_content.slice(this.question_metrics_snapshot.pasted_content_count)
            };

            // Calculate WPM for this question
            if (questionMetrics.text_input.words_added > 0 && question_duration_ms > 0) {
                const minutes = question_duration_ms / 60000;
                questionMetrics.text_input.wpm_for_question = questionMetrics.text_input.words_added / minutes;
            }

            return questionMetrics;
        }

        // Calculate suspicion score only for the question duration
        calculateSuspicionScoreForQuestion() {
            if (!this.question_start_time) return 0;

            return this.calculateSuspicionScoreForInterval(
                this.question_start_time,
                Date.now()
            );
        }

        // Reset question metrics (call this after collecting)
        resetQuestionMetrics() {
            this.question_start_time = null;
            this.question_metrics_snapshot = null;
            this.is_question_active = false;
        }

        // Add this method to get current suspicion score
        getCurrentSuspicionScore() {
            this.calculateSuspicionScore();
            return this.suspicion_score;
        }

        // Add this method to the InterviewMetrics class
        startMonacoMouseMonitoring(editorContainer) {
            if (!editorContainer) return;

            const self = this;

            // Monaco-specific event handlers
            const handleMonacoMouseMove = (event) => {
                // Forward to existing mouse movement handler
                self.handleMouseMove(event);
            };

            const handleMonacoMouseClick = (event) => {
                self.handleMouseClick(event);

                // Check if click is inside Monaco editor
                if (editorContainer.contains(event.target)) {
                    // Monaco editor is considered the answer area
                    // Don't count as outside
                } else {
                    self.mouse_input.clicks_outside_answer_area++;
                }
            };

            const handleMonacoContextMenu = (event) => {
                // Always count right-clicks in Monaco
                self.mouse_input.right_click_count++;
                self.recordActivity();
            };

            const handleMonacoMouseDown = (event) => {
                self.handleMouseDown(event);
            };

            const handleMonacoMouseUp = (event) => {
                self.handleMouseUp(event);
            };

            // Listen for Monaco-specific events
            editorContainer.addEventListener('mousemove', handleMonacoMouseMove, { passive: true });
            editorContainer.addEventListener('click', handleMonacoMouseClick);
            editorContainer.addEventListener('contextmenu', handleMonacoContextMenu);
            editorContainer.addEventListener('mousedown', handleMonacoMouseDown);
            editorContainer.addEventListener('mouseup', handleMonacoMouseUp);

            // Store cleanup function for Monaco-specific listeners
            this.mouse_input.monacoCleanupListeners = () => {
                if (editorContainer) {
                    editorContainer.removeEventListener('mousemove', handleMonacoMouseMove);
                    editorContainer.removeEventListener('click', handleMonacoMouseClick);
                    editorContainer.removeEventListener('contextmenu', handleMonacoContextMenu);
                    editorContainer.removeEventListener('mousedown', handleMonacoMouseDown);
                    editorContainer.removeEventListener('mouseup', handleMonacoMouseUp);
                }
            };
        }

        // Calculate suspicion score based on specified metrics
        calculateSuspicionScore() {
            // If we have an interval start time, calculate for that interval
            if (this.interval_start_time) {
                return this.calculateSuspicionScoreForInterval(
                    this.interval_start_time,
                    Date.now()
                );
            }

            // Otherwise calculate for entire session
            return this.calculateSuspicionScoreForInterval(
                this.session_start_time || Date.now(),
                Date.now()
            );
        }

        getAppSwitchesForInterval(startTime, endTime) {
            if (!this.visibility.app_switch_timestamps) {
                return 0;
            }
            return this.visibility.app_switch_timestamps.filter(timestamp =>
                timestamp >= startTime && timestamp <= endTime
            ).length;
        }

        calculateSuspicionScoreForInterval(startTime, endTime) {
            let score = 0;
            const maxScore = 100;

            // Weights for different metrics
            const weights = {
                // Highest priority: Pasted content (max 60 points)
                pastedContentNotFromQuestion: 60,

                // High priority: Visibility metrics (max 30 points)
                appSwitchCount: 20,
                unfocusedDuration: 10,

                // Medium priority: Environment integrity (max 10 points)
                devToolsIndicators: 4,
                windowResizeCount: 3,
                consoleUsage: 3
            };

            // Filter pasted content ONLY for this interval
            const intervalPastedContent = this.pasted_content.filter(paste => {
                const pasteTime = new Date(paste.timestamp).getTime();
                return pasteTime >= startTime && pasteTime <= endTime;
            });

            // 1. Analyze pasted_content in THIS interval only (Highest priority)
            if (intervalPastedContent && intervalPastedContent.length > 0) {
                // Count pastes that are NOT from question IN THIS INTERVAL
                const nonQuestionPastes = intervalPastedContent.filter(
                    paste => !paste.is_from_question
                ).length;

                if (nonQuestionPastes > 0) {
                    // Base penalty for any non-question paste - high impact
                    let pasteScore = Math.min(nonQuestionPastes * 25, weights.pastedContentNotFromQuestion);

                    // Additional penalty for large pastes IN THIS INTERVAL
                    const largePastes = intervalPastedContent.filter(
                        paste => !paste.is_from_question && paste.is_large_paste
                    ).length;

                    pasteScore += largePastes * 10;

                    // Additional penalty for formatted content (likely code) IN THIS INTERVAL
                    const formattedPastes = intervalPastedContent.filter(
                        paste => !paste.is_from_question && paste.is_formatted
                    ).length;

                    pasteScore += formattedPastes * 8;

                    // High penalty for URL pattern pastes IN THIS INTERVAL
                    const urlPastes = intervalPastedContent.filter(
                        paste => !paste.is_from_question && paste.has_url_patterns
                    ).length;

                    pasteScore += urlPastes * 15;

                    // Cap at weight maximum
                    score += Math.min(pasteScore, weights.pastedContentNotFromQuestion);
                }
            }

            // 2. Calculate visibility metrics for THIS interval only
            // We need to calculate unfocused duration and app switches that happened in this interval

            const intervalUnfocusedPeriods = this.visibility.unfocused_periods.filter(period => {
                const periodStart = new Date(period.unfocused_on).getTime();
                const periodEnd = new Date(period.unfocused_till).getTime();
                // Check if the unfocused period overlaps with our interval
                return periodStart < endTime && periodEnd > startTime;
            });

            // Calculate total unfocused time IN THIS INTERVAL
            let intervalUnfocusedDuration = 0;
            intervalUnfocusedPeriods.forEach(period => {
                const periodStart = Math.max(new Date(period.unfocused_on).getTime(), startTime);
                const periodEnd = Math.min(new Date(period.unfocused_till).getTime(), endTime);
                intervalUnfocusedDuration += periodEnd - periodStart;
            });

            // Calculate app switches IN THIS INTERVAL (we'll need to track this separately)
            // For now, we'll use a simplified approach - you might need to track app switches per interval

            // For simplicity, let's create a method to get interval-specific metrics
            // You'll need to track when app switches happened
            const intervalAppSwitches = this.getAppSwitchesForInterval(startTime, endTime);

            // App switch count analysis FOR THIS INTERVAL
            if (intervalAppSwitches > 0) {
                let appSwitchScore = 0;

                if (intervalAppSwitches >= 5) {
                    // 5 or more switches: very high suspicion
                    appSwitchScore = weights.appSwitchCount;
                } else if (intervalAppSwitches >= 3) {
                    // 3-4 switches: high suspicion
                    appSwitchScore = weights.appSwitchCount * 0.8;
                } else if (intervalAppSwitches >= 2) {
                    // 2 switches: medium suspicion
                    appSwitchScore = weights.appSwitchCount * 0.5;
                } else {
                    // 1 switch: low suspicion
                    appSwitchScore = weights.appSwitchCount * 0.2;
                }

                // We can't easily track consecutive switches per interval, so skip that for now
                score += Math.min(appSwitchScore, weights.appSwitchCount);
            }

            // Unfocused duration analysis FOR THIS INTERVAL
            if (intervalUnfocusedDuration > 0) {
                const unfocusedMinutes = intervalUnfocusedDuration / 60000;

                let unfocusedScore = 0;

                if (unfocusedMinutes > 3) {
                    // More than 3 minutes: high penalty
                    unfocusedScore = weights.unfocusedDuration;
                } else if (unfocusedMinutes > 1.5) {
                    // 1.5-3 minutes: medium penalty
                    unfocusedScore = weights.unfocusedDuration * 0.7;
                } else if (unfocusedMinutes > 0.5) {
                    // 30 seconds to 1.5 minutes: low penalty
                    unfocusedScore = weights.unfocusedDuration * 0.4;
                } else {
                    // Less than 30 seconds: minimal penalty
                    unfocusedScore = weights.unfocusedDuration * 0.1;
                }

                score += Math.min(unfocusedScore, weights.unfocusedDuration);
            }

            // 3. Environment integrity metrics are already tracked per event
            // We need to store timestamps for these events to filter by interval

            // For now, we'll need to add timestamp tracking for these events
            // Let's add helper methods to track when these events occurred

            // Cap the score at 100
            return Math.min(Math.round(score), maxScore);
        }

        // Add a method to track app switch timestamps
        recordAppSwitch() {
            const now = Date.now();
            if (!this.visibility.app_switch_timestamps) {
                this.visibility.app_switch_timestamps = [];
            }
            this.visibility.app_switch_timestamps.push(now);
        }

        // Create empty interval metrics structure
        createIntervalSnapshot() {
            return {
                timestamp: Date.now(),
                text_input: {
                    char_typed_count: this.text_input.char_typed_count,
                    backspace_count: this.text_input.backspace_count,
                    paste_count: this.text_input.paste_count,
                    chars_pasted_count: this.text_input.chars_pasted_count,
                    copy_event_count: this.text_input.copy_event_count,
                    total_words: this.text_input.total_words,
                    wpm: this.text_input.wpm
                },
                mouse_input: {
                    click_count: this.mouse_input.click_count,
                    hover_pause_count: this.mouse_input.hover_pause_count,
                    mouse_distance_px: this.mouse_input.mouse_distance_px,
                    right_click_count: this.mouse_input.right_click_count,
                    clicks_outside_answer_area: this.mouse_input.clicks_outside_answer_area
                },
                activity: {
                    total_active_time_ms: this.activity.total_active_time_ms,
                    idle_duration_ms: this.activity.idle_duration_ms
                },
                visibility: {
                    unfocused_duration_ms: this.visibility.unfocused_duration_ms,
                    app_switch_count: this.visibility.app_switch_count
                },
                environment_integrity: {
                    dev_tools_indicators: this.environment_integrity.dev_tools_indicators,
                    window_resize_count: this.environment_integrity.window_resize_count,
                    console_usage_detected: this.environment_integrity.console_usage_detected
                },
                suspicion_score: this.suspicion_score
            };
        }

        // Start new interval tracking
        startNewInterval() {
            const now = Date.now();
            this.last_interval_snapshot = this.createIntervalSnapshot();
            this.interval_start_time = now;
            return true;
        }

        // Get metrics for the current interval (since last interval start)
        getIntervalMetrics() {
            const now = Date.now();

            if (!this.last_interval_snapshot || !this.interval_start_time) {
                this.startNewInterval();
                return null;
            }

            const interval_duration_ms = now - this.interval_start_time;
            if (interval_duration_ms < 100) { // Minimum 100ms interval
                return null;
            }

            // Calculate suspicion score ONLY for this interval
            const intervalSuspicionScore = this.calculateSuspicionScoreForInterval(
                this.interval_start_time,
                now
            );

            // Calculate deltas from the last snapshot
            const intervalMetrics = {
                // Interval metadata
                interview_key: this.interview_key,
                ai_interview_id: this.ai_interview_id,
                question_id: this.current_question_id,
                interval_start_time: toISOString(this.interval_start_time),
                interval_end_time: toISOString(now),
                interval_duration_ms: interval_duration_ms,
                is_interval_metric: true,

                // Text input deltas (only what happened in this interval)
                text_input: {
                    char_typed_count: this.text_input.char_typed_count - this.last_interval_snapshot.text_input.char_typed_count,
                    backspace_count: this.text_input.backspace_count - this.last_interval_snapshot.text_input.backspace_count,
                    paste_count: this.text_input.paste_count - this.last_interval_snapshot.text_input.paste_count,
                    chars_pasted_count: this.text_input.chars_pasted_count - this.last_interval_snapshot.text_input.chars_pasted_count,
                    copy_event_count: this.text_input.copy_event_count - this.last_interval_snapshot.text_input.copy_event_count,
                    words_added: this.text_input.total_words - this.last_interval_snapshot.text_input.total_words,
                    wpm_in_interval: 0
                },

                // Mouse input deltas
                mouse_input: {
                    click_count: this.mouse_input.click_count - this.last_interval_snapshot.mouse_input.click_count,
                    hover_pause_count: this.mouse_input.hover_pause_count - this.last_interval_snapshot.mouse_input.hover_pause_count,
                    mouse_distance_px: this.mouse_input.mouse_distance_px - this.last_interval_snapshot.mouse_input.mouse_distance_px,
                    right_click_count: this.mouse_input.right_click_count - this.last_interval_snapshot.mouse_input.right_click_count,
                    clicks_outside_answer_area: this.mouse_input.clicks_outside_answer_area - this.last_interval_snapshot.mouse_input.clicks_outside_answer_area
                },

                // Activity deltas
                activity: {
                    total_active_time_ms: this.activity.total_active_time_ms - this.last_interval_snapshot.activity.total_active_time_ms,
                    idle_duration_ms: this.activity.idle_duration_ms - this.last_interval_snapshot.activity.idle_duration_ms
                },

                // Visibility deltas
                visibility: {
                    unfocused_duration_ms: this.visibility.unfocused_duration_ms - this.last_interval_snapshot.visibility.unfocused_duration_ms,
                    app_switch_count: this.visibility.app_switch_count - this.last_interval_snapshot.visibility.app_switch_count
                },

                // Environment integrity deltas
                environment_integrity: {
                    dev_tools_indicators: this.environment_integrity.dev_tools_indicators - this.last_interval_snapshot.environment_integrity.dev_tools_indicators,
                    window_resize_count: this.environment_integrity.window_resize_count - this.last_interval_snapshot.environment_integrity.window_resize_count,
                    console_usage_detected: this.environment_integrity.console_usage_detected - this.last_interval_snapshot.environment_integrity.console_usage_detected
                },

                // Suspicion score for THIS INTERVAL only
                suspicion_score: intervalSuspicionScore,

                // Filter pasted content in this interval
                pasted_content: this.pasted_content.filter(paste => {
                    const pasteTime = new Date(paste.timestamp).getTime();
                    return pasteTime >= this.interval_start_time && pasteTime <= now;
                })
            };

            // Calculate WPM for this interval
            if (intervalMetrics.text_input.words_added > 0 && interval_duration_ms > 0) {
                const minutes = interval_duration_ms / 60000;
                intervalMetrics.text_input.wpm_in_interval = intervalMetrics.text_input.words_added / minutes;
            }

            return intervalMetrics;
        }

        getFinalIntervalMetrics() {
            const intervalMetrics = this.getIntervalMetrics();
            // Start fresh interval immediately after getting final metrics
            this.startNewInterval();
            return intervalMetrics;
        }

        // Set current question ID
        setCurrentQuestionId(questionId) {
            this.current_question_id = questionId;
        }

        // Set AI interview ID
        setAIInterviewId(aiInterviewId) {
            console.log("in in in in", aiInterviewId);

            this.ai_interview_id = aiInterviewId;
        }

        startSession() {
            const now = Date.now();
            this.session_start_time = now;
            this.session_end_time = null;
            this.activity.last_activity_time = now;

            // Start interval tracking
            this.startNewInterval();

            // Start monitoring for inactivity
            this.startInactivityMonitoring();

            // Start visibility monitoring
            this.startVisibilityMonitoring();

            // Start mouse monitoring
            this.startMouseMonitoring();

            // Start environment integrity monitoring
            this.startEnvironmentIntegrityMonitoring();

            // Start copy monitoring
            this.startCopyMonitoring();

            // Start Monaco detection
            this.setupMonacoDetection();
        }

        setInterviewKey(interviewKey) {
            this.interview_key = interviewKey;
        }

        startEnvironmentIntegrityMonitoring() {
            this.startDevToolsDetection();
            this.startConsoleMonitoring();
            this.startWindowResizeMonitoring();
        }

        startCopyMonitoring() {
            const handleCopy = (event) => {
                const selection = window.getSelection();
                const copiedText = selection.toString().trim();

                // Only count if something meaningful was copied
                if (copiedText.length > 0) {
                    this.text_input.copy_event_count++;

                    // Updated: Remove recordActivity with parameters
                    this.recordActivity();
                }
            };

            // Add copy event listener to document
            document.addEventListener('copy', handleCopy);

            // Store cleanup function
            this.text_input.copy_cleanup_listener = () => {
                document.removeEventListener('copy', handleCopy);
            };
        }

        isTextFromAnswer(copiedText) {
            const answerText = this.getCurrentText();
            if (!answerText) return false;

            const normalizedAnswer = answerText.toLowerCase().replace(/\s+/g, ' ');
            const normalizedCopied = copiedText.toLowerCase().replace(/\s+/g, ' ');

            return normalizedAnswer.includes(normalizedCopied) ||
                normalizedCopied.includes(normalizedAnswer) ||
                this.calculateSimilarity(normalizedAnswer, normalizedCopied) > 0.7;
        }

        isTextFromQuestion(copiedText) {
            if (!question) return false;

            // Use the same improved logic
            return this.isContentFromQuestion(copiedText);
        }

        // Detect developer tools opening
        startDevToolsDetection() {
            const self = this;

            // Method 1: Check for console open using timing attack
            const checkConsoleOpen = () => {
                const start = performance.now();
                const end = performance.now();

                // If console is open, the execution time will be significantly longer
                if (end - start > 100) {
                    self.environment_integrity.dev_tools_indicators++;
                    // Updated: Remove recordActivity with parameters
                    self.recordActivity();
                }
            };

            // Method 2: Monitor for F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C
            const handleKeyDown = (event) => {
                const key = event.key;
                const ctrl = event.ctrlKey || event.metaKey;
                const shift = event.shiftKey;

                // F12 key
                if (key === 'F12') {
                    event.preventDefault();
                    self.environment_integrity.dev_tools_indicators++;
                    // Updated: Remove recordActivity with parameters
                    self.recordActivity();
                }

                // Ctrl+Shift+I or Cmd+Shift+I (Mac)
                if (ctrl && shift && (key === 'I' || key === 'i')) {
                    event.preventDefault();
                    self.environment_integrity.dev_tools_indicators++;
                    // Updated: Remove recordActivity with parameters
                    self.recordActivity();
                }

                // Ctrl+Shift+J or Cmd+Shift+J (Mac)
                if (ctrl && shift && (key === 'J' || key === 'j')) {
                    event.preventDefault();
                    self.environment_integrity.dev_tools_indicators++;
                    // Updated: Remove recordActivity with parameters
                    self.recordActivity();
                }

                // Ctrl+Shift+C or Cmd+Shift+C (Mac)
                if (ctrl && shift && (key === 'C' || key === 'c')) {
                    event.preventDefault();
                    self.environment_integrity.dev_tools_indicators++;
                    // Updated: Remove recordActivity with parameters
                    self.recordActivity();
                }
            };

            // Method 3: Check for window resize (devtools often changes window size)
            let lastWidth = window.innerWidth;
            let lastHeight = window.innerHeight;

            const checkResize = () => {
                const width = window.innerWidth;
                const height = window.innerHeight;

                // Significant resize (likely devtools)
                if (Math.abs(width - lastWidth) > 100 || Math.abs(height - lastHeight) > 100) {
                    self.environment_integrity.dev_tools_indicators++;
                    // Updated: Remove recordActivity with parameters
                    self.recordActivity();
                }

                lastWidth = width;
                lastHeight = height;
            };

            // Method 4: Check if console object methods are different (advanced detection)
            const checkConsoleMethods = () => {
                try {
                    // Override console.clear to detect when devtools are opened
                    const originalClear = console.clear;
                    console.clear = function () {
                        self.environment_integrity.dev_tools_indicators++;
                        // Updated: Remove recordActivity with parameters
                        self.recordActivity();
                        return originalClear.apply(this, arguments);
                    };

                    self.environment_integrity.original_console_clear = originalClear;
                } catch (error) {
                    // Ignore errors in console method checking
                }
            };

            // Method 5: Monitor for right-click inspect element
            document.addEventListener('contextmenu', (event) => {
                // If right-click is followed by rapid devtools opening, we can detect it
                setTimeout(() => {
                    checkConsoleOpen();
                }, 100);
            });

            // Set up all detection methods
            try {
                // Keydown listener for shortcuts
                document.addEventListener('keydown', handleKeyDown);
                self.environment_integrity.keydown_listener = handleKeyDown;

                // Regular checks
                self.environment_integrity.dev_tools_check_interval = setInterval(() => {
                    checkConsoleOpen();
                    checkResize();
                }, 3000); // Check every 3 seconds

                // Initial console method check
                checkConsoleMethods();

            } catch (error) {
                console.warn('Some dev tools detection methods failed:', error);
            }
        }

        // Monitor console usage - SIMPLE WHITELIST VERSION
        startConsoleMonitoring() {
            const self = this;

            // Store the original console methods
            const originalConsole = {
                log: console.log,
                error: console.error,
                warn: console.warn,
                info: console.info
            };

            // Patterns that we know come from our code (whitelist)
            const ourCodePatterns = [
                'DevTools detection check',
                'Interview Metrics:',
                'Cheating Probability Score:',
                'DOM mutation monitoring failed:',
                'Dev tools detection limited:',
                'Some dev tools detection methods failed:',
                'useInterviewMetrics',
                'InterviewMetrics'
            ];

            const consoleHandler = (method, args) => {
                // Convert all arguments to string for pattern matching
                const argsString = args.map(arg =>
                    typeof arg === 'string' ? arg : JSON.stringify(arg)
                ).join(' ');

                // Check if this console call matches any of our known patterns
                const isOurCode = ourCodePatterns.some(pattern =>
                    argsString.includes(pattern)
                );

                // Only count console usage if it's NOT from our code
                if (!isOurCode) {
                    self.environment_integrity.console_usage_detected++;
                    // Updated: Remove recordActivity with parameters
                    self.recordActivity();
                }

                // Call original console method
                originalConsole[method].apply(console, args);
            };

            // Override console methods
            ['log', 'error', 'warn', 'info'].forEach(method => {
                console[method] = (...args) => consoleHandler(method, args);
            });

            this.environment_integrity.console_override = originalConsole;
        }

        // Monitor window resizing
        startWindowResizeMonitoring() {
            let resizeTimeout;

            const handleResize = () => {
                this.environment_integrity.window_resize_count++;

                // Debounce resize events
                if (resizeTimeout) clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    // Updated: Remove recordActivity with parameters
                    this.recordActivity();
                }, 250);
            };

            window.addEventListener('resize', handleResize);
            this.environment_integrity.resize_listener = handleResize;
        }

        // Clean up environment integrity monitoring
        cleanupEnvironmentIntegrityMonitoring() {
            // Clean up dev tools detection
            if (this.environment_integrity.dev_tools_check_interval) {
                clearInterval(this.environment_integrity.dev_tools_check_interval);
            }

            // Clean up keydown listener
            if (this.environment_integrity.keydown_listener) {
                document.removeEventListener('keydown', this.environment_integrity.keydown_listener);
            }

            // Clean up console monitoring
            if (this.environment_integrity.console_override) {
                console.log = this.environment_integrity.console_override.log;
                console.error = this.environment_integrity.console_override.error;
                console.warn = this.environment_integrity.console_override.warn;
                console.info = this.environment_integrity.console_override.info;
            }

            // Clean up console.clear override
            if (this.environment_integrity.original_console_clear) {
                console.clear = this.environment_integrity.original_console_clear;
            }

            // Clean up resize listener
            if (this.environment_integrity.resize_listener) {
                window.removeEventListener('resize', this.environment_integrity.resize_listener);
            }
        }

        // Mouse Input Tracking methods
        startMouseMonitoring() {
            const handleMouseMove = (event) => this.handleMouseMove(event);
            const handleMouseClick = (event) => this.handleMouseClick(event);
            const handleContextMenu = (event) => this.handleContextMenu(event);
            const handleMouseDown = (event) => this.handleMouseDown(event);
            const handleMouseUp = (event) => this.handleMouseUp(event);
            const handleScroll = (event) => this.handleScroll(event);
            const handleSelectionChange = () => this.handleSelectionChange();

            // Add event listeners
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('click', handleMouseClick);
            document.addEventListener('contextmenu', handleContextMenu);
            document.addEventListener('mousedown', handleMouseDown);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('scroll', handleScroll);
            document.addEventListener('selectionchange', handleSelectionChange);

            // Store cleanup function
            this.mouse_input.cleanupListeners = () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('click', handleMouseClick);
                document.removeEventListener('contextmenu', handleContextMenu);
                document.removeEventListener('mousedown', handleMouseDown);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('scroll', handleScroll);
                document.removeEventListener('selectionchange', handleSelectionChange);
            };

            // Start mouse idle monitoring
            this.startMouseIdleMonitoring();
        }


        // Add these helper methods to the InterviewMetrics class:
        isInMonacoEditor(element) {
            if (!element) return false;

            // Check if element or its ancestors have Monaco-specific classes
            let current = element;
            while (current && current !== document.body) {
                if (current.classList) {
                    // Monaco editor specific classes
                    if (current.classList.contains('monaco-editor') ||
                        current.classList.contains('overflowingContentWidgets') ||
                        current.classList.contains('monaco-scrollable-element') ||
                        current.classList.contains('view-lines') ||
                        current.classList.contains('view-line') ||
                        current.classList.contains('mtk1')) {
                        return true;
                    }
                }
                current = current.parentElement;
            }
            return false;
        }

        isInTextarea(element) {
            if (!element) return false;

            let current = element;
            while (current && current !== document.body) {
                if (current.tagName && current.tagName.toLowerCase() === 'textarea') {
                    return true;
                }
                current = current.parentElement;
            }
            return false;
        }

        // Handle mouse movement
        handleMouseMove(event) {
            const now = Date.now();
            const currentPosition = { x: event.clientX, y: event.clientY };

            // Calculate distance moved
            if (this.mouse_input.last_mouse_position.x !== 0 && this.mouse_input.last_mouse_position.y !== 0) {
                const distance = Math.sqrt(
                    Math.pow(currentPosition.x - this.mouse_input.last_mouse_position.x, 2) +
                    Math.pow(currentPosition.y - this.mouse_input.last_mouse_position.y, 2)
                );

                this.mouse_input.mouse_distance_px += distance;

                // Detect mouse teleportation (large sudden movements)
                if (distance > this.mouse_input.teleport_threshold) {
                    this.mouse_input.mouse_teleport_count++;
                }
            }

            // Handle mouse idle tracking
            if (this.mouse_input.mouse_idle_start_time) {
                const idleDuration = now - this.mouse_input.mouse_idle_start_time;
                if (idleDuration >= this.mouse_input.mouse_idle_threshold) {
                    this.recordMouseIdlePeriod(this.mouse_input.mouse_idle_start_time, now);
                }
                this.mouse_input.mouse_idle_start_time = null;
            }

            // Handle hover tracking
            const targetElement = event.target;
            if (this.mouse_input.current_hover_element !== targetElement) {
                if (this.mouse_input.hover_start_time) {
                    const hoverDuration = now - this.mouse_input.hover_start_time;
                    if (hoverDuration >= this.mouse_input.hover_pause_threshold) {
                        this.mouse_input.hover_pause_count++;
                    }
                }
                this.mouse_input.current_hover_element = targetElement;
                this.mouse_input.hover_start_time = now;
            }

            this.mouse_input.last_mouse_position = currentPosition;
            this.mouse_input.last_mouse_move_time = now;

            // Updated: Remove recordActivity with parameters
            this.recordActivity();
        }

        // Add this method to find Monaco editor elements
        setupMonacoDetection() {
            const self = this;

            // Try to find Monaco editor using MutationObserver
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        mutation.addedNodes.forEach((node) => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                const monacoElement = node.querySelector &&
                                    node.querySelector('.monaco-editor, .monaco-scrollable-element');
                                if (monacoElement) {
                                    console.log('Monaco editor detected, setting up enhanced monitoring');
                                    self.enhanceMonacoMonitoring(monacoElement);
                                }
                            }
                        });
                    }
                });
            });

            // Start observing
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });

            // Store observer for cleanup
            this.mouse_input.monacoObserver = observer;
        }

        enhanceMonacoMonitoring(monacoElement) {
            if (!monacoElement) return;

            const self = this;

            // Add event listeners directly to Monaco element
            const handleMonacoClick = (event) => {
                // Mark as Monaco click for special handling
                event._isMonacoEvent = true;
                self.handleMouseClick(event);
            };

            const handleMonacoContextMenu = (event) => {
                event._isMonacoEvent = true;
                self.handleContextMenu(event);
            };

            monacoElement.addEventListener('click', handleMonacoClick);
            monacoElement.addEventListener('contextmenu', handleMonacoContextMenu);

            // Store for cleanup
            if (!this.mouse_input.monacoEventListeners) {
                this.mouse_input.monacoEventListeners = [];
            }

            this.mouse_input.monacoEventListeners.push({
                element: monacoElement,
                click: handleMonacoClick,
                contextmenu: handleMonacoContextMenu
            });
        }

        // Handle mouse clicks
        handleMouseClick(event) {
            this.mouse_input.click_count++;
            const now = Date.now();

            // Track click timestamps for pattern analysis
            this.mouse_input.click_timestamps.push(now);

            // Enhanced Monaco detection
            const isInMonaco = this.isInMonacoEditor(event.target);
            const isInTextarea = this.isInTextarea(event.target);

            // Determine if click is inside answer area
            const isInsideAnswerArea = isInTextarea || isInMonaco;

            // Count clicks outside answer area
            if (!isInsideAnswerArea) {
                this.mouse_input.clicks_outside_answer_area++;

                // Check if it's a suspicious click (e.g., on UI elements like suggestions)
                if (event.target.classList &&
                    event.target.classList.contains('monaco-hover') ||
                    event.target.classList.contains('suggest-widget') ||
                    event.target.classList.contains('parameter-hints-widget')) {
                    // These are Monaco UI elements that should be considered part of the answer area
                    this.mouse_input.clicks_outside_answer_area--; // Decrement since it's actually part of Monaco
                }
            }

            // Detect right clicks - IMPROVED
            if (event.type === 'contextmenu' || event.button === 2) {
                this.mouse_input.right_click_count++;
            }

            // Detect suspicious click patterns (rapid clicking)
            if (this.mouse_input.click_timestamps.length > 3) {
                const recentClicks = this.mouse_input.click_timestamps.slice(-4);
                const intervals = [];
                for (let i = 1; i < recentClicks.length; i++) {
                    intervals.push(recentClicks[i] - recentClicks[i - 1]);
                }
                const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
                if (avgInterval < 200) { // Very rapid clicking
                    this.mouse_input.suspicious_click_patterns++;
                }
            }

            this.recordActivity();
        }

        // Handle right clicks
        handleContextMenu(event) {
            // Always count context menu events
            this.mouse_input.right_click_count++;

            // Check if it's in Monaco editor
            if (this.isInMonacoEditor(event.target)) {
                // You might want to log Monaco-specific right clicks
                console.log('Right click detected in Monaco editor');
            }

            this.recordActivity();
        }

        // In handleMouseDown - store click start position
        handleMouseDown(event) {
            this.mouse_input.mouse_down_position = {
                x: event.clientX,
                y: event.clientY
            };
        }

        // Helper method to detect if user is actually selecting text (not just clicking)
        isActualTextSelection(startPosition, endPosition) {
            // If mouse moved significantly during click, it's likely a selection
            const distance = Math.sqrt(
                Math.pow(endPosition.x - startPosition.x, 2) +
                Math.pow(endPosition.y - startPosition.y, 2)
            );

            // Consider it a selection if mouse moved more than 5 pixels
            return distance > 5;
        }

        // In handleMouseUp - measure precision
        handleMouseUp(event) {
            const now = Date.now();
            const currentPosition = { x: event.clientX, y: event.clientY };

            // Precision click detection
            if (this.mouse_input.mouse_down_position) {
                const distance = Math.sqrt(
                    Math.pow(currentPosition.x - this.mouse_input.mouse_down_position.x, 2) +
                    Math.pow(currentPosition.y - this.mouse_input.mouse_down_position.y, 2)
                );

                // Bot detection: If movement is extremely small AND clicking same position multiple times
                if (distance <= this.mouse_input.precision_threshold) {
                    // Check if this is clicking the same position repeatedly
                    const isSamePosition = this.isClickingSamePosition(currentPosition);

                    if (isSamePosition) {
                        this.mouse_input.precision_clicks++;
                        // Updated: Remove recordActivity with parameters
                        this.recordActivity();
                    }
                }

                // Track this click position
                this.trackClickPosition(currentPosition, now);

                this.mouse_input.mouse_down_position = null;
            }
        }

        // Ultra-conservative handleSelectionChange method
        handleSelectionChange() {
            const selection = window.getSelection();
            const selectedText = selection.toString().trim();

            // Only proceed if there's actual selected text (not empty or just cursor)
            if (selectedText.length > 0) {
                const now = Date.now();

                // Ultra-conservative: Only count a selection if:
                // 1. It's different text from the last selection, OR
                // 2. At least 1 second has passed since the last selection

                const shouldCountSelection =
                    !this.mouse_input.last_selected_text ||
                    selectedText !== this.mouse_input.last_selected_text ||
                    (this.mouse_input.last_selection_time &&
                        (now - this.mouse_input.last_selection_time) > 1000);

                if (shouldCountSelection) {
                    // Check for rapid selections
                    if (this.mouse_input.last_selection_time) {
                        const timeBetweenSelections = now - this.mouse_input.last_selection_time;
                        if (timeBetweenSelections < 2000) {
                            // Updated: Remove recordActivity with parameters
                            this.recordActivity();
                        }
                    }

                    this.mouse_input.last_selection_time = now;
                    this.mouse_input.last_selected_text = selectedText;

                    // Updated: Remove recordActivity with parameters
                    this.recordActivity();
                }
            }
        }

        isClickingSamePosition(currentPosition) {
            if (this.mouse_input.recent_click_positions.length < 2) {
                return false; // Need at least 2 clicks to compare
            }

            const recentPositions = this.mouse_input.recent_click_positions.slice(-3); // Last 3 clicks

            // Count how many recent clicks are at the same position (within tolerance)
            const samePositionCount = recentPositions.filter(pos =>
                Math.abs(pos.x - currentPosition.x) <= this.mouse_input.position_tolerance &&
                Math.abs(pos.y - currentPosition.y) <= this.mouse_input.position_tolerance
            ).length;

            return samePositionCount >= this.mouse_input.max_same_position_clicks;
        }

        // Track click positions
        trackClickPosition(position, timestamp) {
            this.mouse_input.recent_click_positions.push({
                x: position.x,
                y: position.y,
                timestamp: timestamp
            });

            // Keep only recent clicks (last 10 clicks, last 30 seconds)
            const thirtySecondsAgo = timestamp - 30000;
            this.mouse_input.recent_click_positions = this.mouse_input.recent_click_positions.filter(
                pos => pos.timestamp > thirtySecondsAgo
            ).slice(-10); // Keep max 10 positions
        }

        // Get count of clicks at same position
        getSamePositionCount(currentPosition) {
            return this.mouse_input.recent_click_positions.filter(pos =>
                Math.abs(pos.x - currentPosition.x) <= this.mouse_input.position_tolerance &&
                Math.abs(pos.y - currentPosition.y) <= this.mouse_input.position_tolerance
            ).length;
        }

        // Handle scrolling
        handleScroll(event) {
            const now = Date.now();
            this.mouse_input.scroll_timestamps.push(now);

            // Detect scroll anomalies (erratic scrolling)
            if (this.mouse_input.scroll_timestamps.length > 5) {
                const recentScrolls = this.mouse_input.scroll_timestamps.slice(-6);
                const intervals = [];
                for (let i = 1; i < recentScrolls.length; i++) {
                    intervals.push(recentScrolls[i] - recentScrolls[i - 1]);
                }

                const variance = intervals.reduce((acc, interval) => {
                    const mean = intervals.reduce((a, b) => a + b, 0) / intervals.length;
                    return acc + Math.pow(interval - mean, 2);
                }, 0) / intervals.length;

                if (variance > 1000000) { // High variance indicates erratic scrolling
                    this.mouse_input.scroll_behavior_anomalies++;
                }
            }

            // Updated: Remove recordActivity with parameters
            this.recordActivity();
        }

        // Start mouse idle monitoring
        startMouseIdleMonitoring() {
            this.mouse_input.mouse_idle_interval = setInterval(() => {
                const now = Date.now();
                if (this.mouse_input.last_mouse_move_time &&
                    (now - this.mouse_input.last_mouse_move_time) >= this.mouse_input.mouse_idle_threshold &&
                    !this.mouse_input.mouse_idle_start_time) {
                    this.mouse_input.mouse_idle_start_time = this.mouse_input.last_mouse_move_time + this.mouse_input.mouse_idle_threshold;
                }
            }, 1000); // Check every second
        }

        // Record mouse idle period
        recordMouseIdlePeriod(idleFrom, idleTill) {
            const idlePeriod = {
                idle_from: toISOString(idleFrom),
                idle_till: toISOString(idleTill),
                idle_duration: idleTill - idleFrom
            };

            this.mouse_input.mouse_idle_periods.push(idlePeriod);
            // Updated: Remove recordActivity with parameters
            this.recordActivity();
        }

        // Start visibility monitoring
        startVisibilityMonitoring() {
            const handleVisibilityChange = () => {
                if (document.hidden) {
                    this.handleFocusLoss();
                } else {
                    this.handleFocusGain();
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);
            window.addEventListener('blur', () => this.handleFocusLoss());
            window.addEventListener('focus', () => this.handleFocusGain());

            this.visibility.cleanupListeners = () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                window.removeEventListener('blur', () => this.handleFocusLoss());
                window.removeEventListener('focus', () => this.handleFocusGain());
            };
        }

        // Handle focus loss (user switched tabs or minimized window)
        handleFocusLoss() {
            const now = Date.now();

            // Only record if not already unfocused
            if (!this.visibility.last_focus_loss_time) {
                this.visibility.last_focus_loss_time = now;
                this.visibility.app_switch_count++;

                // Record the timestamp
                this.recordAppSwitch();

                // Check for consecutive switches
                if (this.visibility.last_switch_time &&
                    (now - this.visibility.last_switch_time) < this.visibility.rapid_switch_threshold) {
                    this.visibility.consecutive_switches++;
                }

                this.visibility.last_switch_time = now;
                this.recordActivity();
            }
        }

        // Handle focus gain (user returned to tab)
        handleFocusGain() {
            const now = Date.now();

            // Only record if we were previously unfocused
            if (this.visibility.last_focus_loss_time) {
                const unfocusedDuration = now - this.visibility.last_focus_loss_time;

                // Record the unfocused period
                const unfocusedPeriod = {
                    unfocused_on: toISOString(this.visibility.last_focus_loss_time),
                    unfocused_till: toISOString(now),
                    unfocused_duration: unfocusedDuration
                };

                this.visibility.unfocused_periods.push(unfocusedPeriod);

                // Update metrics
                this.visibility.unfocused_duration_ms += unfocusedDuration;

                // Update max unfocused duration
                if (unfocusedDuration > this.visibility.max_unfocused_duration_ms) {
                    this.visibility.max_unfocused_duration_ms = unfocusedDuration;
                }

                // Update min unfocused duration
                if (this.visibility.min_unfocused_duration_ms === null ||
                    unfocusedDuration < this.visibility.min_unfocused_duration_ms) {
                    this.visibility.min_unfocused_duration_ms = unfocusedDuration;
                }

                // Reset focus loss time
                this.visibility.last_focus_loss_time = null;

                // Updated: Remove recordActivity with parameters
                this.recordActivity();
            }
        }

        // Start inactivity monitoring
        startInactivityMonitoring() {
            // Set up event listeners for user activity
            const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
            const handleActivity = () => this.handleUserActivity();

            activityEvents.forEach(event => {
                document.addEventListener(event, handleActivity, { passive: true });
            });

            // Store cleanup function
            this.activity.cleanupListeners = () => {
                activityEvents.forEach(event => {
                    document.removeEventListener(event, handleActivity);
                });
            };

            // Start the first inactivity check
            this.scheduleInactivityCheck();
        }

        // Handle user activity
        handleUserActivity() {
            const now = Date.now();
            const lastActivityTime = this.activity.last_activity_time;

            // Update last activity time
            this.activity.last_activity_time = now;

            // Clear existing timeout and schedule new one
            if (this.activity.inactivity_timeout) {
                clearTimeout(this.activity.inactivity_timeout);
            }
            this.scheduleInactivityCheck();
        }

        // Schedule inactivity check
        scheduleInactivityCheck() {
            this.activity.inactivity_timeout = setTimeout(() => {
                const now = Date.now();
                const timeSinceLastActivity = now - this.activity.last_activity_time;

                if (timeSinceLastActivity >= 20000) {
                    // User is idle for 20 seconds
                    this.recordInactivityPeriod(this.activity.last_activity_time, now);
                }
            }, 20000); // Check every 20 seconds
        }

        // Record inactivity period
        recordInactivityPeriod(idleFrom, idleTill) {
            const inactivityPeriod = {
                idle_from: toISOString(idleFrom),
                idle_till: toISOString(idleTill)
            };

            this.activity.inactivity_history.push(inactivityPeriod);

            // Update total idle duration
            const idleDuration = idleTill - idleFrom;
            this.activity.idle_duration_ms += idleDuration;

            // Updated: Remove recordActivity with parameters
            this.recordActivity();
        }

        // UPDATED: recordActivity now only updates last_activity_time
        recordActivity() {
            const timestamp = Date.now();
            this.activity.last_activity_time = timestamp;
            this.activity.total_active_time_ms += 1; // Minimal activity time
        }

        recordKeystroke(key, isBackspace = false) {
            if (isBackspace) {
                this.text_input.backspace_count++;
            } else {
                this.text_input.char_typed_count++;
            }

            this.recordActivity();
        }

        calculateWPM(text, startTime, endTime) {
            const words = text.trim().split(/\s+/).filter(word => word.length > 0);
            const wordCount = words.length;
            const minutes = (endTime - startTime) / 60000;

            return minutes > 0 ? wordCount / minutes : 0;
        }

        analyzeTypingSpeedConsistency() {
            // Simple heuristic: if WPM varies significantly from typical human typing (30-80 WPM)
            if (this.text_input.wpm > 0) {
                if (this.text_input.wpm < 10 || this.text_input.wpm > 150) {
                    this.text_input.inconsistent_typing_speed++;
                }
            }
        }

        analyzeSpellingAndTechnicalTerms(text) {
            // Simplified - only detect if text contains technical terms
            const words = text.toLowerCase().split(/\s+/).filter(word => word.length > 0);
            const uniqueWords = [...new Set(words)];

            // Count technical terms
            const technicalWords = uniqueWords.filter(word =>
                technicalTerms.includes(word.toLowerCase())
            );

            // Track if technical terms are present (simplified)
            if (technicalWords.length > 0) {
                this.text_input.external_content_indicators = Math.max(
                    this.text_input.external_content_indicators,
                    technicalWords.length
                );
            }
        }

        calculateTotalWords(text) {
            if (!text || text.trim() === '') {
                return 0;
            }

            // Split by whitespace and filter out empty strings
            const words = text.trim().split(/\s+/).filter(word => word.length > 0);
            return words.length;
        }

        updateTotalWords(newText) {
            const currentWordCount = this.calculateTotalWords(newText);

            // Only update if there's a meaningful change (more than just whitespace changes)
            if (currentWordCount !== this.text_input.last_word_count) {
                this.text_input.total_words = currentWordCount;
                this.text_input.last_word_count = currentWordCount;

                // Record word count changes for analysis
                if (currentWordCount > 0) {
                    // Updated: Remove recordActivity with parameters
                    this.recordActivity();
                }
            }
        }

        recordPaste(content, charCount, isFromQuestion = false) {
            this.text_input.paste_count++;
            this.text_input.chars_pasted_count += charCount;

            // Calculate isFromQuestion with the new improved method
            const calculatedIsFromQuestion = this.isContentFromQuestion(content);

            // Enhanced debug logging
            // console.log('=== PASTE DETECTION DEBUG ===');
            // console.log('Content length:', content.length);
            // console.log('Content preview:', content.substring(0, 200));
            // console.log('Question preview:', questionRef.current ? questionRef.current.substring(0, 200) : 'No question');
            // console.log('Is from question (calculated):', calculatedIsFromQuestion);
            // console.log('=== END DEBUG ===');

            // Add to pasted_content array
            const pasteEvent = {
                content: content,
                char_count: charCount,
                timestamp: toISOString(Date.now()),
                is_from_question: calculatedIsFromQuestion, // Use calculated value
                has_code_patterns: this.detectCodePatterns(content),
                has_url_patterns: this.detectUrlPatterns(content),
                is_large_paste: charCount > 50,
                is_formatted: this.detectFormattedContent(content),
                words_pasted: this.calculateTotalWords(content)
            };

            this.pasted_content.push(pasteEvent);

            // Count URL pattern pastes
            if (pasteEvent.has_url_patterns) {
                this.text_input.url_pattern_pastes++;
            }

            // Only increment external_content_indicators if NOT from question
            if (!calculatedIsFromQuestion) {
                this.text_input.external_content_indicators++;
            }

            // Update paste-to-type ratio
            this.text_input.paste_to_type_ratio = this.text_input.chars_pasted_count /
                Math.max(this.text_input.char_typed_count, 1);

            this.recordActivity();
        }

        detectCodePatterns(content) {
            const codePatterns = [/function\s+\w+/, /const\s+\w+\s*=/, /let\s+\w+\s*=/, /var\s+\w+\s*=/,
                /if\s*\(/, /for\s*\(/, /while\s*\(/, /return\s+/, /console\.log/,
                /<\/?\w+>/, /class\s+\w+/, /import\s+.+from/, /=>/, /\{.*\}/,
                /\/\/.*|\/\*[\s\S]*?\*\//, /\.\w+\(/, /\[\s*\]/, /\{\s*\}/];
            return codePatterns.some(pattern => pattern.test(content));
        }

        detectUrlPatterns(content) {
            // Only match unambiguous URLs
            const urlPatterns = [
                // Protocol URLs
                /\bhttps?:\/\/[^\s<"]+/,

                // www.domain.tld/path
                /\bwww\.[a-z0-9-]+\.[a-z]{2,}(?:\/[^\s<"]*)?/i,

                // Domain with path (must have a forward slash after domain)
                /\b[a-z0-9-]+\.[a-z]{2,}\/[^\s<"]+/i,

                // localhost with port
                /\blocalhost:\d+(?:\/[^\s<"]*)?/,
                /\b127\.0\.0\.1:\d+(?:\/[^\s<"]*)?/,

                // file:// URLs
                /\bfile:\/\/\/[^\s<"]+/
            ];

            return urlPatterns.some(pattern => pattern.test(content));
        }

        detectFormattedContent(content) {
            if (!content || content.length < 50) return false;

            const lines = content.split('\n');
            if (lines.length < 3) return false;

            // Multiple detection strategies with weights
            const indicators = {
                // Strong indicators
                hasCodeBlocks: /```[\s\S]*```|^\s*(def |class |function |if |for |while |switch |import |export |const |let |var )/m.test(content),
                hasConsistentIndent: false,
                hasTabs: /\t/.test(content),
                hasMultipleSpacesIndent: false,
                hasSemicolonEndings: false,
                hasBraces: /[{}]/.test(content),

                // Moderate indicators
                hasCodeComments: /\/\/|\/\*|\*\/|# |<!--|-->/.test(content),
                hasImports: /\b(import|require|from)\b/.test(content),
                highSpecialCharDensity: false
            };

            // Check for consistent indentation
            let indentedLines = 0;
            for (let i = 0; i < Math.min(lines.length, 20); i++) {
                const line = lines[i];
                if (line.match(/^\t/)) {
                    indentedLines++;
                } else if (line.match(/^ {2,}/)) {
                    const spaces = line.match(/^( +)/)[1].length;
                    if (spaces >= 2 && (spaces % 2 === 0 || spaces % 4 === 0)) {
                        indentedLines++;
                    }
                }

                // Check for lines ending with semicolons (not preceded by space)
                if (line.trim().match(/;[ \t]*$/)) {
                    indicators.hasSemicolonEndings = true;
                }
            }

            indicators.hasConsistentIndent = indentedLines >= 3;
            indicators.hasMultipleSpacesIndent = indentedLines > 0;

            // Check special character density
            const specialChars = content.match(/[{}()\[\];=<>+\-*/%&|^~!]/g);
            if (specialChars) {
                indicators.highSpecialCharDensity = (specialChars.length / content.length) > 0.04;
            }

            // Count positive indicators
            const positiveCount = Object.values(indicators).filter(Boolean).length;
            const strongIndicators = [
                indicators.hasCodeBlocks,
                indicators.hasConsistentIndent,
                indicators.hasBraces,
                indicators.hasImports
            ].filter(Boolean).length;

            // Decision logic
            return strongIndicators >= 2 || positiveCount >= 4;
        }

        // Replace the existing isContentFromQuestion method with this improved version:
        // Replace the existing isContentFromQuestion method with this version
        isContentFromQuestion(content) {
            if (!content || !content.trim()) return false;

            const currentQuestion = questionRef.current;
            if (!currentQuestion) return false;

            // Debug logging
            console.log('Checking if content is from question:', {
                contentLength: content.length,
                questionLength: currentQuestion.length,
                contentPreview: content.substring(0, 100),
                questionPreview: currentQuestion.substring(0, 100)
            });

            // Normalize both strings for comparison
            const normalizeString = (str) => {
                if (!str) return '';
                return str
                    .toLowerCase()
                    .replace(/\r\n/g, '\n') // Normalize line endings
                    .replace(/\r/g, '\n')   // Normalize line endings
                    .replace(/\s+/g, ' ')   // Replace all whitespace with single space
                    .replace(/[^\w\s.,!?;:'"-]/g, ' ') // Remove special characters
                    .trim();
            };

            const normalizedContent = normalizeString(content);
            const normalizedQuestion = normalizeString(currentQuestion);

            console.log('Normalized comparison:', {
                normalizedContent: normalizedContent.substring(0, 150),
                normalizedQuestion: normalizedQuestion.substring(0, 150)
            });

            // 1. First, try exact substring matching
            if (normalizedQuestion.includes(normalizedContent)) {
                console.log('Found as exact substring match');
                return true;
            }

            // 2. Check if content is a substring of question (handle partial matches)
            // Split into words for more granular matching
            const contentWords = normalizedContent.split(/\s+/).filter(w => w.length > 0);
            const questionWords = normalizedQuestion.split(/\s+/).filter(w => w.length > 0);

            // If content has more than 3 words, check for partial matches
            if (contentWords.length > 3) {
                // Try sliding window approach
                for (let i = 0; i <= questionWords.length - contentWords.length; i++) {
                    const windowWords = questionWords.slice(i, i + contentWords.length);
                    const windowText = windowWords.join(' ');

                    if (this.calculateStringSimilarity(windowText, normalizedContent) > 0.95) {
                        console.log('Found via sliding window match');
                        return true;
                    }
                }
            }

            // 3. Calculate similarity
            const similarity = this.calculateStringSimilarity(normalizedContent, normalizedQuestion);
            console.log('Similarity score:', similarity);

            // 4. Word overlap check
            const contentWordSet = new Set(contentWords.filter(w => w.length > 3));
            const questionWordSet = new Set(questionWords.filter(w => w.length > 3));

            let commonWords = 0;
            for (const word of contentWordSet) {
                if (questionWordSet.has(word)) {
                    commonWords++;
                }
            }

            const wordOverlapPercentage = contentWordSet.size > 0 ? (commonWords / contentWordSet.size) : 0;
            console.log('Word overlap:', { commonWords, totalContentWords: contentWordSet.size, percentage: wordOverlapPercentage });

            // Decision logic - be more lenient for question content
            if (similarity > 0.7 || wordOverlapPercentage > 0.6) {
                console.log('Content is likely from question based on similarity/overlap');
                return true;
            }

            // 5. Check for direct text overlap (case-insensitive, ignoring extra whitespace)
            const simplifiedContent = content.toLowerCase().replace(/\s+/g, ' ').trim();
            const simplifiedQuestion = currentQuestion.toLowerCase().replace(/\s+/g, ' ').trim();

            if (simplifiedQuestion.includes(simplifiedContent)) {
                console.log('Found via simplified includes check');
                return true;
            }

            console.log('Content not detected as from question');
            return false;
        }

        // Add this helper method for better string comparison
        calculateStringSimilarity(str1, str2) {
            if (!str1 || !str2) return 0;

            // Use Jaro-Winkler similarity for better accuracy
            const jaroWinklerDistance = (s1, s2) => {
                if (s1 === s2) return 1.0;

                const m = this.jaroDistance(s1, s2);
                const p = 0.1; // Scaling factor
                const l = this.commonPrefixLength(s1, s2, 4);

                return m + (l * p * (1 - m));
            };

            return jaroWinklerDistance(str1, str2);
        }

        jaroDistance(s1, s2) {
            // Simplified Jaro distance calculation
            const s1Len = s1.length;
            const s2Len = s2.length;

            if (s1Len === 0 || s2Len === 0) return 0;

            const matchDistance = Math.floor(Math.max(s1Len, s2Len) / 2) - 1;
            let matches = 0;
            const s1Matches = new Array(s1Len).fill(false);
            const s2Matches = new Array(s2Len).fill(false);

            // Count matches
            for (let i = 0; i < s1Len; i++) {
                const start = Math.max(0, i - matchDistance);
                const end = Math.min(i + matchDistance + 1, s2Len);

                for (let j = start; j < end; j++) {
                    if (!s2Matches[j] && s1[i] === s2[j]) {
                        s1Matches[i] = true;
                        s2Matches[j] = true;
                        matches++;
                        break;
                    }
                }
            }

            if (matches === 0) return 0;

            // Count transpositions
            let transpositions = 0;
            let k = 0;
            for (let i = 0; i < s1Len; i++) {
                if (s1Matches[i]) {
                    while (!s2Matches[k]) k++;
                    if (s1[i] !== s2[k]) transpositions++;
                    k++;
                }
            }

            return (matches / s1Len + matches / s2Len + (matches - transpositions / 2) / matches) / 3;
        }

        commonPrefixLength(s1, s2, maxLength) {
            let i = 0;
            while (i < maxLength && i < s1.length && i < s2.length && s1[i] === s2[i]) {
                i++;
            }
            return i;
        }

        // Also update the calculateSimilarity method to be more accurate:
        calculateSimilarity(str1, str2) {
            if (!str1 || !str2) return 0;

            // Simple similarity calculation using Jaccard index for better accuracy
            const words1 = new Set(str1.toLowerCase().split(/\s+/).filter(w => w.length > 0));
            const words2 = new Set(str2.toLowerCase().split(/\s+/).filter(w => w.length > 0));

            if (words1.size === 0 || words2.size === 0) return 0;

            const intersection = new Set([...words1].filter(x => words2.has(x)));
            const union = new Set([...words1, ...words2]);

            return intersection.size / union.size;
        }

        levenshteinDistance(str1, str2) {
            // Basic Levenshtein distance implementation
            const matrix = Array(str2.length + 1).fill(null).map(() =>
                Array(str1.length + 1).fill(null));

            for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
            for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;

            for (let j = 1; j <= str2.length; j++) {
                for (let i = 1; i <= str1.length; i++) {
                    const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
                    matrix[j][i] = Math.min(
                        matrix[j][i - 1] + 1,
                        matrix[j - 1][i] + 1,
                        matrix[j - 1][i - 1] + indicator
                    );
                }
            }

            return matrix[str2.length][str1.length];
        }

        updateTextMetrics(newText, oldText) {
            const newLength = newText.length;
            const oldLength = oldText.length;
            const delta = newLength - oldLength;

            // Detect unexplained text deltas (not from typing or pasting)
            if (delta > 1 && !this.isRecentPaste) {
                this.text_input.unexplained_text_delta += delta;
            }

            this.isRecentPaste = false;

            // Update total words count
            this.updateTotalWords(newText);

            // Analyze spelling and technical terms
            this.analyzeSpellingAndTechnicalTerms(newText);

            // Calculate WPM based on session time
            const currentTime = Date.now();
            if (this.session_start_time) {
                const currentWPM = this.calculateWPM(
                    newText,
                    this.session_start_time,
                    currentTime
                );

                if (currentWPM > 0) {
                    this.text_input.wpm = currentWPM;
                    this.analyzeTypingSpeedConsistency();
                }
            }

            this.text_input.last_text_length = newLength;
        }

        calculateTextComplexity(text) {
            const words = text.split(/\s+/).filter(word => word.length > 0);
            const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);

            if (words.length === 0) return 0;

            const avgSentenceLength = words.length / sentences.length;
            const avgWordLength = text.replace(/\s+/g, '').length / words.length;
            const longWordRatio = words.filter(word => word.length > 6).length / words.length;

            return (avgSentenceLength * 0.4) + (avgWordLength * 0.4) + (longWordRatio * 0.2);
        }

        calculateFinalMetrics() {
            const sessionEndTime = Date.now();

            // Set session end time
            this.session_end_time = sessionEndTime;

            const sessionStartTime = this.session_start_time;
            this.session_duration_ms = sessionEndTime - sessionStartTime;

            // Calculate suspicion score before finalizing
            this.calculateSuspicionScore();

            // Record any ongoing unfocused period
            if (this.visibility.last_focus_loss_time) {
                this.handleFocusGain();
            }

            // Record any ongoing mouse idle period
            if (this.mouse_input.mouse_idle_start_time) {
                this.recordMouseIdlePeriod(this.mouse_input.mouse_idle_start_time, sessionEndTime);
                this.mouse_input.mouse_idle_start_time = null;
            }

            if (this.mouse_input.monacoCleanupListeners) {
                this.mouse_input.monacoCleanupListeners();
            }

            // Clean up environment integrity monitoring
            this.cleanupEnvironmentIntegrityMonitoring();

            // Clean up event listeners
            if (this.activity.cleanupListeners) {
                this.activity.cleanupListeners();
            }
            if (this.visibility.cleanupListeners) {
                this.visibility.cleanupListeners();
            }
            // Clean up mouse listeners
            if (this.mouse_input.cleanupListeners) {
                this.mouse_input.cleanupListeners();
            }
            if (this.mouse_input.mouse_idle_interval) {
                clearInterval(this.mouse_input.mouse_idle_interval);
            }

            // Clear inactivity timeout
            if (this.activity.inactivity_timeout) {
                clearTimeout(this.activity.inactivity_timeout);
            }

            // Set defaults for empty metrics
            if (this.activity.inactivity_history.length === 0) {
                this.activity.idle_duration_ms = 0;
            }
            if (this.visibility.min_unfocused_duration_ms === null) {
                this.visibility.min_unfocused_duration_ms = 0;
            }

            // Calculate overall WPM if not already set
            if (this.text_input.wpm === 0 && this.session_duration_ms > 0) {
                const minutes = this.session_duration_ms / 60000;
                const wordCount = this.text_input.total_words;
                this.text_input.wpm = minutes > 0 ? wordCount / minutes : 0;
            }

            // Ensure total_words is up to date in final metrics
            if (this.text_input.total_words === 0) {
                this.updateTotalWords(this.getCurrentText());
            }

            if (this.mouse_input.monacoObserver) {
                this.mouse_input.monacoObserver.disconnect();
            }

            // Clean up Monaco event listeners
            if (this.mouse_input.monacoEventListeners) {
                this.mouse_input.monacoEventListeners.forEach(listener => {
                    if (listener.element && listener.click) {
                        listener.element.removeEventListener('click', listener.click);
                    }
                    if (listener.element && listener.contextmenu) {
                        listener.element.removeEventListener('contextmenu', listener.contextmenu);
                    }
                });
                this.mouse_input.monacoEventListeners = [];
            }
        }

        getCurrentText() {
            return '';
        }

        collectMetricsSnapshot() {
            const sessionEndTime = Date.now();
            const sessionStartTime = this.session_start_time;
            const session_duration_ms = sessionEndTime - sessionStartTime;

            // Calculate current WPM
            let currentWPM = this.text_input.wpm;
            if (currentWPM === 0 && session_duration_ms > 0) {
                const minutes = session_duration_ms / 60000;
                const wordCount = this.text_input.total_words;
                currentWPM = minutes > 0 ? wordCount / minutes : 0;
            }

            // Calculate suspicion score for snapshot
            this.calculateSuspicionScore();

            // Return snapshot without stopping
            return {
                interview_key: this.interview_key,
                ai_interview_id: this.ai_interview_id, // Add ai_interview_id here
                is_browser: this.is_browser,
                platform: this.platform,
                session_start_time: toISOString(this.session_start_time),
                session_end_time: toISOString(sessionEndTime),
                session_duration_ms: session_duration_ms,
                user_agent: this.user_agent,
                suspicion_score: this.suspicion_score, // Add suspicion score here
                activity: {
                    idle_duration_ms: this.activity.idle_duration_ms,
                    last_activity_time: toISOString(this.activity.last_activity_time),
                    total_active_time_ms: this.activity.total_active_time_ms,
                    inactivity_history: this.activity.inactivity_history,
                },
                visibility: {
                    unfocused_duration_ms: this.visibility.unfocused_duration_ms,
                    app_switch_count: this.visibility.app_switch_count,
                    max_unfocused_duration_ms: this.visibility.max_unfocused_duration_ms,
                    min_unfocused_duration_ms: this.visibility.min_unfocused_duration_ms,
                    unfocused_periods: this.visibility.unfocused_periods,
                    consecutive_switches: this.visibility.consecutive_switches
                },
                mouse_input: {
                    click_count: this.mouse_input.click_count,
                    hover_pause_count: this.mouse_input.hover_pause_count,
                    clicks_outside_answer_area: this.mouse_input.clicks_outside_answer_area,
                    right_click_count: this.mouse_input.right_click_count,
                    mouse_idle_periods: this.mouse_input.mouse_idle_periods
                },
                text_input: {
                    char_typed_count: this.text_input.char_typed_count,
                    backspace_count: this.text_input.backspace_count,
                    wpm: currentWPM,
                    paste_count: this.text_input.paste_count,
                    chars_pasted_count: this.text_input.chars_pasted_count,
                    copy_event_count: this.text_input.copy_event_count,
                    external_content_indicators: this.text_input.external_content_indicators,
                    url_pattern_pastes: this.text_input.url_pattern_pastes,
                    total_words: this.text_input.total_words,
                },
                environment_integrity: {
                    dev_tools_indicators: this.environment_integrity.dev_tools_indicators,
                    window_resize_count: this.environment_integrity.window_resize_count,
                    console_usage_detected: this.environment_integrity.console_usage_detected
                },
                pasted_content: this.pasted_content.map(paste => ({
                    ...paste,
                    timestamp: toISOString(paste.timestamp)
                })),
                // event_timeline removed from snapshot
            };
        }

        getMetrics() {
            this.calculateFinalMetrics();

            return {
                interview_key: this.interview_key,
                ai_interview_id: this.ai_interview_id, // Add ai_interview_id here
                is_browser: this.is_browser,
                platform: this.platform,
                session_start_time: toISOString(this.session_start_time),
                session_end_time: toISOString(this.session_end_time),
                session_duration_ms: this.session_duration_ms,
                user_agent: this.user_agent,
                suspicion_score: this.suspicion_score, // Add suspicion score here
                activity: {
                    idle_duration_ms: this.activity.idle_duration_ms,
                    last_activity_time: toISOString(this.activity.last_activity_time),
                    total_active_time_ms: this.activity.total_active_time_ms,
                    inactivity_history: this.activity.inactivity_history,
                },
                visibility: {
                    unfocused_duration_ms: this.visibility.unfocused_duration_ms,
                    app_switch_count: this.visibility.app_switch_count,
                    max_unfocused_duration_ms: this.visibility.max_unfocused_duration_ms,
                    min_unfocused_duration_ms: this.visibility.min_unfocused_duration_ms,
                    unfocused_periods: this.visibility.unfocused_periods,
                    consecutive_switches: this.visibility.consecutive_switches
                },
                // Include mouse input metrics (without removed ones)
                mouse_input: {
                    click_count: this.mouse_input.click_count,
                    hover_pause_count: this.mouse_input.hover_pause_count,
                    clicks_outside_answer_area: this.mouse_input.clicks_outside_answer_area,
                    right_click_count: this.mouse_input.right_click_count,
                    mouse_idle_periods: this.mouse_input.mouse_idle_periods
                },
                text_input: {
                    char_typed_count: this.text_input.char_typed_count,
                    backspace_count: this.text_input.backspace_count,
                    wpm: this.text_input.wpm,
                    paste_count: this.text_input.paste_count,
                    chars_pasted_count: this.text_input.chars_pasted_count,
                    copy_event_count: this.text_input.copy_event_count,
                    external_content_indicators: this.text_input.external_content_indicators,
                    url_pattern_pastes: this.text_input.url_pattern_pastes,
                    total_words: this.text_input.total_words,
                },
                // Include environment integrity metrics (without removed ones)
                environment_integrity: {
                    dev_tools_indicators: this.environment_integrity.dev_tools_indicators,
                    window_resize_count: this.environment_integrity.window_resize_count,
                    console_usage_detected: this.environment_integrity.console_usage_detected
                },
                pasted_content: this.pasted_content.map(paste => ({
                    ...paste,
                    timestamp: toISOString(paste.timestamp)
                })),
                // event_timeline removed from final metrics
                // Include interval metrics summary - ADD SAFETY CHECK
                interval_summary: {
                    total_intervals: this.interval_metrics ? this.interval_metrics.length : 0,
                    intervals: this.interval_metrics || []
                }
            };
        }
    }

    // Create metrics instance with interviewKey
    const metrics = useRef(new InterviewMetrics(interviewKey));

    // Update interview key when prop changes
    useEffect(() => {
        if (metrics.current && interviewKey) {
            metrics.current.setInterviewKey(interviewKey);
        }
    }, [interviewKey]);

    const startMonitoring = useCallback((monacoContainer = null) => {
        if (interviewKey) {
            metrics.current.setInterviewKey(interviewKey);
        }

        metrics.current.startSession();

        // Start Monaco-specific monitoring if container is provided
        if (monacoContainer && metrics.current.startMonacoMouseMonitoring) {
            metrics.current.startMonacoMouseMonitoring(monacoContainer);
        }

        setIsMonitoring(true);
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [interviewKey]);

    const stopMonitoring = useCallback(() => {
        setIsMonitoring(false);
        if (metrics.current) {
            try {
                const finalMetrics = metrics.current.getMetrics();
                console.log('Interview Metrics (debug only):', finalMetrics);
                const cheatingScore = calculateCheatingProbability(finalMetrics);
                console.log('Cheating Probability Score (debug):', cheatingScore);
            } catch (error) {
                console.error('Error calculating final metrics:', error);
            }
        }
    }, []);

    const resetMonitoring = useCallback((preserveAnswer = true) => {
        if (metrics.current) {
            // Clean up all listeners
            if (metrics.current.activity?.cleanupListeners) {
                metrics.current.activity.cleanupListeners();
            }
            if (metrics.current.visibility?.cleanupListeners) {
                metrics.current.visibility.cleanupListeners();
            }
            if (metrics.current.mouse_input?.cleanupListeners) {
                metrics.current.mouse_input.cleanupListeners();
            }
            if (metrics.current.text_input?.copy_cleanup_listener) {
                metrics.current.text_input.copy_cleanup_listener();
            }
            if (metrics.current.activity?.inactivity_timeout) {
                clearTimeout(metrics.current.activity.inactivity_timeout);
            }
            if (metrics.current.mouse_input?.mouse_idle_interval) {
                clearInterval(metrics.current.mouse_input.mouse_idle_interval);
            }
            if (metrics.current.environment_integrity) {
                metrics.current.cleanupEnvironmentIntegrityMonitoring();
            }
        }

        // Create new instance with current interviewKey
        metrics.current = new InterviewMetrics(interviewKey);
        setIsMonitoring(false);
        setPasteDialog({ open: false, pasteData: null });

        // Restore the answer if preserveAnswer is true
        if (preserveAnswer) {
            const currentAnswer = preservedAnswerRef.current;
            if (currentAnswer) {
                setTimeout(() => {
                    setAnswer(currentAnswer);
                }, 0);
            }
        } else {
            setAnswer('');
        }
    }, [interviewKey]);

    const handleAnswerChange = useCallback((event) => {
        const newText = event?.target?.value;
        const oldText = answer;

        if (isMonitoring) {
            metrics.current.updateTextMetrics(newText, oldText);
            metrics.current.recordActivity();
        }

        setAnswer(newText);
    }, [isMonitoring, answer]);

    const handleKeyDown = useCallback((event) => {
        if (!isMonitoring) return;

        const isBackspace = event.key === 'Backspace';
        metrics.current.recordKeystroke(event.key, isBackspace);
    }, [isMonitoring]);

    const handlePasteConfirm = useCallback((pasteData, isFromQuestion = false) => {
        if (!pasteData) return;

        const charCount = pasteData.length;
        metrics.current.recordPaste(pasteData, charCount, isFromQuestion);
        metrics.current.isRecentPaste = true;

        const currentAnswer = answerRef.current || '';
        const selection = lastPasteSelectionRef.current;

        let newText;

        if (
            selection &&
            typeof selection.start === 'number' &&
            typeof selection.end === 'number'
        ) {
            const { start, end } = selection;
            newText =
                currentAnswer.slice(0, start) +
                pasteData +
                currentAnswer.slice(end);
        } else {
            newText = currentAnswer + pasteData;
        }

        setAnswer(newText);

        if (isMonitoring) {
            metrics.current.updateTextMetrics(newText, currentAnswer);
        }

        setPasteDialog({ open: false, pasteData: null, isFromQuestion: false });
        lastPasteSelectionRef.current = null;
    }, [isMonitoring]);

    const handlePaste = useCallback((event) => {
        if (!isMonitoring) return;

        event.preventDefault();
        event.stopPropagation();

        let pasteData = '';
        try {
            if (event.clipboardData) {
                pasteData = event.clipboardData.getData('text');
            } else if (window.clipboardData) {
                pasteData = window.clipboardData.getData('Text');
            }
        } catch (err) {
            console.error('Error accessing clipboard:', err);
        }

        if (!pasteData.trim()) return;

        const target = event.target || {};
        const start =
            typeof target.selectionStart === 'number'
                ? target.selectionStart
                : null;
        const end =
            typeof target.selectionEnd === 'number'
                ? target.selectionEnd
                : null;

        lastPasteSelectionRef.current = { start, end };

        const charCount = pasteData.length;
        const isFromQuestion = metrics.current.isContentFromQuestion(pasteData);

        handlePasteConfirm(pasteData, isFromQuestion);
    }, [isMonitoring, handlePasteConfirm]);

    const handlePasteDialogConfirm = useCallback((shouldPaste) => {
        if (shouldPaste && pasteDialog.pasteData) {
            handlePasteConfirm(pasteDialog.pasteData, false);
        } else {
            setPasteDialog({ open: false, pasteData: null, isFromQuestion: false });
        }
    }, [pasteDialog.pasteData, handlePasteConfirm]);

    // NEW: Get interval metrics (only last 30 seconds)
    const getIntervalMetrics = useCallback(() => {
        if (!metrics.current || !isMonitoring) return null;
        return metrics.current.getIntervalMetrics();
    }, [isMonitoring]);

    // Get FINAL interval metrics for submit/skip
    const getFinalIntervalMetrics = useCallback(() => {
        if (!metrics.current || !isMonitoring) return null;
        return metrics.current.getFinalIntervalMetrics();
    }, [isMonitoring]);

    // Start new interval
    const startNewInterval = useCallback(() => {
        if (!metrics.current || !isMonitoring) return;
        metrics.current.startNewInterval();
    }, [isMonitoring]);

    // NEW: Set current question ID
    const setCurrentQuestionId = useCallback((questionId) => {
        console.log("id in setCurrentQuestionId", questionId);
        if (metrics.current) {
            metrics.current.setCurrentQuestionId(questionId);
        }
    }, []);

    // NEW: Set AI interview ID
    const setAIInterviewId = useCallback((aiInterviewId) => {
        if (metrics.current) {
            metrics.current.setAIInterviewId(aiInterviewId);
        }
    }, []);

    // Keep this for backward compatibility if needed
    const collectMetricsSnapshot = useCallback(() => {
        if (!metrics.current || !isMonitoring) return null;
        return metrics.current.collectMetricsSnapshot();
    }, [isMonitoring]);

    // Add to the return statement at the bottom
    // In useInterviewMetrics hook return statement
    return {
        answer,
        setAnswer,
        isMonitoring,
        pasteDialog,
        textareaRef,
        questionRef,
        startMonitoring,
        stopMonitoring,
        resetMonitoring,
        handleAnswerChange,
        handleKeyDown,
        handlePaste,
        handlePasteDialogConfirm,
        getIntervalMetrics,
        getFinalIntervalMetrics,
        startNewInterval,
        setCurrentQuestionId,
        setAIInterviewId,
        getCurrentSuspicionScore: useCallback(() => {
            if (!metrics.current) return 0;
            return metrics.current.getCurrentSuspicionScore();
        }, []),
        // NEW: Question-level metrics methods
        startQuestionMetrics: useCallback((questionId) => {
            if (metrics.current) {
                metrics.current.startQuestionMetrics(questionId);
            }
        }, []),
        getQuestionMetrics: useCallback((action = 'submit') => {
            if (!metrics.current) return null;
            return metrics.current.getQuestionMetrics(action);
        }, []),
        resetQuestionMetrics: useCallback(() => {
            if (metrics.current) {
                metrics.current.resetQuestionMetrics();
            }
        }, [])
    };
};

// Updated helper function to calculate cheating probability with new metrics
function calculateCheatingProbability(metrics) {
    let score = 0;
    const weights = {
        pasteToTypeRatio: 0.12,
        rapidTypingBursts: 0.10,
        largePasteEvents: 0.08,
        unexplainedTextDelta: 0.07,
        inconsistentTypingSpeed: 0.06,
        externalContentIndicators: 0.05,
        longIdlePeriods: 0.03,
        activityBursts: 0.03,
        urlPatternPastes: 0.02,
        formattedContentPastes: 0.02,
        copyEventCount: 0.01,
        // Visibility weights
        appSwitchCount: 0.04,
        consecutiveSwitches: 0.03,
        maxUnfocusedDuration: 0.02,
        // Mouse weights
        mouseTeleport: 0.03,
        suspiciousClicks: 0.02,
        rightClicks: 0.02,
        precisionClicks: 0.02,
        clicksOutside: 0.02,
        scrollAnomalies: 0.01,
        // Environment integrity weights
        domMutations: 0.05,
        suspiciousDOMChanges: 0.06,
        devToolsIndicators: 0.07,
        windowResizeCount: 0.03,
        consoleUsage: 0.04,
        jsInjectionAttempts: 0.08,
        networkAnomalies: 0.06
    };

    // Add safety checks for all metric accesses
    const text_input = metrics.text_input || {};
    const activity = metrics.activity || {};
    const visibility = metrics.visibility || {};
    const mouse_input = metrics.mouse_input || {};
    const environment_integrity = metrics.environment_integrity || {};

    // Normalize and calculate weighted score with safety checks
    if ((text_input.paste_to_type_ratio || 0) > 0.3) score += weights.pasteToTypeRatio;
    if ((text_input.rapid_typing_bursts || 0) > 5) score += weights.rapidTypingBursts;
    if ((text_input.external_content_indicators || 0) > 0) score += weights.externalContentIndicators;
    if ((text_input.url_pattern_pastes || 0) > 0) score += weights.urlPatternPastes;
    if ((activity.long_idle_periods || 0) > 3) score += weights.longIdlePeriods;
    if ((activity.activity_bursts || 0) > 5) score += weights.activityBursts;

    // Visibility-based cheating indicators with safety checks
    if ((visibility.app_switch_count || 0) > 5) score += weights.appSwitchCount;
    if ((visibility.consecutive_switches || 0) > 3) score += weights.consecutiveSwitches;
    if ((visibility.max_unfocused_duration_ms || 0) > 30000) score += weights.maxUnfocusedDuration;

    // Mouse-based indicators
    if ((mouse_input.mouse_teleport_count || 0) > 5) score += weights.mouseTeleport;
    if ((mouse_input.suspicious_click_patterns || 0) > 3) score += weights.suspiciousClicks;
    if ((mouse_input.right_click_count || 0) > 5) score += weights.rightClicks;
    if ((mouse_input.precision_clicks || 0) > 10) score += weights.precisionClicks;
    if ((mouse_input.clicks_outside_answer_area || 0) > 10) score += weights.clicksOutside;
    if ((mouse_input.scroll_behavior_anomalies || 0) > 2) score += weights.scrollAnomalies;

    // Environment integrity indicators
    if ((environment_integrity.dom_mutation_count || 0) > 10) score += weights.domMutations;
    if ((environment_integrity.suspicious_dom_changes || 0) > 0) score += weights.suspiciousDOMChanges;
    if ((environment_integrity.dev_tools_indicators || 0) > 0) score += weights.devToolsIndicators;
    if ((environment_integrity.window_resize_count || 0) > 5) score += weights.windowResizeCount;
    if ((environment_integrity.console_usage_detected || 0) > 0) score += weights.consoleUsage;
    if ((environment_integrity.javascript_injection_attempts || 0) > 0) score += weights.jsInjectionAttempts;
    if ((environment_integrity.network_request_anomalies || 0) > 0) score += weights.networkAnomalies;

    return Math.min(score * 100, 100).toFixed(2);
}